import socketService, { RESYNC_EVENT } from '../services/socket'

/**
 * Ressincronização das telas com o servidor.
 *
 * No celular, com o app em segundo plano, o sistema congela a aba: o socket
 * morre sem avisar e os eventos dessa janela (mensagem nova, chat novo na fila)
 * se perdem. Ao voltar, a lista mostrava o estado de quando saiu. Aqui:
 *  - voltar ao primeiro plano / rede voltar → força a reconexão do socket e
 *    avisa as telas para buscarem de novo;
 *  - o socket reconectar sozinho também avisa (ver services/socket.ts).
 *
 * `origem` permite que quem disparou (ex.: o pull-to-refresh de uma lista)
 * ignore o próprio aviso e não busque duas vezes.
 */

/** Tempo mínimo em segundo plano para valer uma nova busca ao voltar. */
const MIN_OCULTO_MS = 2000

export function onResync(aoSincronizar: (origem?: string) => void): () => void {
  const handler = (event: Event) => {
    aoSincronizar((event as CustomEvent<{ origem?: string }>).detail?.origem)
  }
  window.addEventListener(RESYNC_EVENT, handler)
  return () => window.removeEventListener(RESYNC_EVENT, handler)
}

export function dispararResync(origem?: string): void {
  socketService.ensureConnected()
  window.dispatchEvent(new CustomEvent(RESYNC_EVENT, { detail: { origem } }))
}

let instalado = false

/** Instala uma vez (idempotente) os gatilhos de "voltei para o app". */
export function instalarResyncAoVoltar(): void {
  if (instalado || typeof document === 'undefined') return
  instalado = true

  let ocultoDesde = 0
  document.addEventListener('visibilitychange', () => {
    if (document.visibilityState === 'hidden') {
      ocultoDesde = Date.now()
      return
    }
    if (ocultoDesde && Date.now() - ocultoDesde >= MIN_OCULTO_MS) {
      dispararResync('voltou')
    }
    ocultoDesde = 0
  })

  // iOS restaurando a página do cache (bfcache) não dispara visibilitychange.
  window.addEventListener('pageshow', (event) => {
    if ((event as PageTransitionEvent).persisted) dispararResync('voltou')
  })

  window.addEventListener('online', () => dispararResync('rede'))
}
